import type { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

import {
  LELY_TOOLTIP_CARET_NUDGE,
  LELY_TOOLTIP_CARET_SIZE,
  getLelyTooltipArrowColor,
  getLelyTooltipSurfaceStyles,
} from '../../styles/newStyles/lelyTooltipSurfaceStyles';
import { lelyTokenPrimitives } from '@/common/styles/newStyles/LelyMuiTheme';

const t = lelyTokenPrimitives as Record<string, string>;

export type ToggleTipPosition = 'top' | 'bottom' | 'left' | 'right';

export const TOGGLE_TIP_MAX_WIDTH = 300;
export const TOGGLE_TIP_CLOSE_SIZE = 24;

function getGridLayout(hasHeader: boolean, hasLink: boolean) {
  if (hasLink) {
    return {
      areas: hasHeader
        ? '"header close" "content content" "link link"'
        : '"content close" "link link"',
      rows: hasHeader ? 'auto auto auto' : 'auto auto',
    };
  }

  return {
    areas: hasHeader ? '"header close" "content content"' : '"content close"',
    rows: hasHeader ? 'auto auto' : 'auto',
  };
}

function getPointerSx(
  theme: Theme,
  placement: ToggleTipPosition,
  showChevron: boolean,
) {
  if (!showChevron) {
    return {};
  }

  const caret = {
    content: '""',
    position: 'absolute' as const,
    width: LELY_TOOLTIP_CARET_SIZE,
    height: LELY_TOOLTIP_CARET_SIZE,
    backgroundColor: getLelyTooltipArrowColor(theme),
    transform: 'rotate(45deg)',
  };

  const horizontal = {
    left: 'var(--toggle-tip-arrow-offset, 50%)',
    marginLeft: 0,
  };

  const vertical = {
    top: 'var(--toggle-tip-arrow-offset, 50%)',
    marginTop: 0,
  };

  if (placement === 'bottom') {
    return {
      '&::after': { ...caret, top: -LELY_TOOLTIP_CARET_NUDGE, ...horizontal },
    };
  }
  if (placement === 'top') {
    return {
      '&::after': {
        ...caret,
        bottom: -LELY_TOOLTIP_CARET_NUDGE,
        ...horizontal,
      },
    };
  }
  if (placement === 'left') {
    return {
      '&::after': {
        ...caret,
        right: -LELY_TOOLTIP_CARET_NUDGE * 1.1,
        ...vertical,
      },
    };
  }

  return {
    '&::after': { ...caret, left: -LELY_TOOLTIP_CARET_NUDGE, ...vertical },
  };
}

export function getToggleTipPaperSx(
  theme: Theme,
  position: ToggleTipPosition,
  chevron: boolean,
): SystemStyleObject<Theme> {
  return {
    ...getLelyTooltipSurfaceStyles(theme),
    position: 'fixed',
    boxSizing: 'border-box',
    width: 'max-content',
    maxWidth: TOGGLE_TIP_MAX_WIDTH,
    padding: t.spaceSm,
    gap: t.spaceSm,
    alignItems: 'start',
    overflow: 'visible',
    color: theme.palette.text.light,
    '& .MuiTypography-root': { color: 'inherit' },
    ...getPointerSx(theme, position, chevron),
  };
}

export const toggleTipHeaderSx: SystemStyleObject<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const toggleTipCloseButtonSx: SystemStyleObject<Theme> = {
  marginLeft: 'auto',
  minWidth: TOGGLE_TIP_CLOSE_SIZE,
  minHeight: TOGGLE_TIP_CLOSE_SIZE,
  width: TOGGLE_TIP_CLOSE_SIZE,
  height: TOGGLE_TIP_CLOSE_SIZE,
  p: 0,
  flexShrink: 0,
};

export const toggleTipBodySx: SystemStyleObject<Theme> = {
  gridArea: 'content',
  whiteSpace: 'pre-line',
  color: 'inherit',
};

export const toggleTipLinkSx: SystemStyleObject<Theme> = {
  gridArea: 'link',
  color: t.colorWhite100,
  minHeight: 48,
  width: 'fit-content',
  ml: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: t.textBaseFontSize,
  lineHeight: t.textBaseLineHeight,
  textDecoration: 'none',
  '&:hover span': { textDecoration: 'underline' },
};
